import { useState } from 'react'
import clsx from 'clsx'

const fieldClass =
  'mp-field w-full min-h-11 rounded-xl border px-3.5 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-brand-primary focus:ring-4 focus:ring-brand-primary/20 disabled:cursor-not-allowed disabled:bg-slate-100'

export function Card({ className, bodyClassName, children, title, description, action, lift = false }) {
  return (
    <section
      className={clsx(
        'mp-surface overflow-hidden rounded-2xl border shadow-card',
        lift && 'mp-card-lift',
        className,
      )}
    >
      {(title || description || action) && (
        <div className="mp-surface-header flex flex-wrap items-start justify-between gap-3 border-b px-5 py-4 sm:px-6">
          <div>
            {title && <h2 className="font-semibold tracking-tight text-brand-deep">{title}</h2>}
            {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
          </div>
          {action}
        </div>
      )}
      <div className={clsx('mp-surface-body p-5 sm:p-6', bodyClassName)}>{children}</div>
    </section>
  )
}

export function Button({ variant = 'primary', className, children, icon, ...props }) {
  const variants = {
    primary: 'mp-button-primary hover:-translate-y-0.5',
    secondary: 'mp-button-secondary',
    danger: 'mp-button-danger',
    ghost: 'mp-button-ghost',
    outline: 'mp-button-outline',
  }
  return (
    <button
      type="button"
      className={clsx(
        'inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-primary/20 disabled:pointer-events-none disabled:opacity-50',
        variants[variant],
        className,
      )}
      {...props}
    >
      {icon && <i className={clsx('fas', icon)} aria-hidden="true" />}
      {children}
    </button>
  )
}

function FieldLabel({ label, hint, children }) {
  return (
    <label className="block text-sm">
      {label && <span className="mb-1.5 block font-semibold text-slate-700">{label}</span>}
      {children}
      {hint && <span className="mt-1.5 block text-xs leading-5 text-slate-500">{hint}</span>}
    </label>
  )
}

export function Input({ label, hint, className, ...props }) {
  return (
    <FieldLabel label={label} hint={hint}>
      <input className={clsx(fieldClass, className)} {...props} />
    </FieldLabel>
  )
}

export function Select({ label, hint, className, children, ...props }) {
  return (
    <FieldLabel label={label} hint={hint}>
      <select className={clsx(fieldClass, 'appearance-none', className)} {...props}>
        {children}
      </select>
    </FieldLabel>
  )
}

export function Textarea({ label, hint, className, ...props }) {
  return (
    <FieldLabel label={label} hint={hint}>
      <textarea className={clsx(fieldClass, 'resize-y', className)} rows={4} {...props} />
    </FieldLabel>
  )
}

export function Checkbox({ label, description, className, ...props }) {
  return (
    <label className={clsx('mp-choice flex cursor-pointer items-start gap-3 rounded-xl border p-3.5', className)}>
      <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-primary focus:ring-brand-primary" {...props} />
      <span>
        <span className="block text-sm font-semibold text-slate-700">{label}</span>
        {description && <span className="mt-0.5 block text-xs leading-5 text-slate-500">{description}</span>}
      </span>
    </label>
  )
}

export function Badge({ children, tone = 'default' }) {
  const tones = {
    default: 'border-slate-200 bg-slate-100 text-slate-700',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    warning: 'border-amber-200 bg-amber-50 text-amber-800',
    danger: 'border-red-200 bg-red-50 text-red-800',
    info: 'border-blue-200 bg-blue-50 text-blue-800',
  }
  return (
    <span className={clsx('inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold', tones[tone])}>
      {children}
    </span>
  )
}

export function Alert({ type = 'info', children }) {
  const styles = {
    info: ['border-blue-200 bg-blue-50 text-blue-900', 'fa-circle-info'],
    success: ['border-emerald-200 bg-emerald-50 text-emerald-900', 'fa-circle-check'],
    error: ['border-red-200 bg-red-50 text-red-900', 'fa-circle-exclamation'],
    warning: ['border-amber-200 bg-amber-50 text-amber-900', 'fa-triangle-exclamation'],
  }
  return (
    <div className={clsx('flex items-start gap-3 rounded-xl border px-4 py-3 text-sm leading-6', styles[type][0])} role={type === 'error' ? 'alert' : 'status'}>
      <i className={clsx('fas mt-1', styles[type][1])} aria-hidden="true" />
      <div>{children}</div>
    </div>
  )
}

export function Spinner({ label = 'Carregando…' }) {
  return (
    <div className="flex min-h-48 flex-col items-center justify-center gap-3 py-12" role="status">
      <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-blue-100 border-t-brand-primary" />
      <span className="text-sm font-medium text-slate-500">{label}</span>
    </div>
  )
}

export function StatCard({ label, value, sub, icon = 'fa-chart-line', tone = 'blue' }) {
  const tones = {
    blue: 'from-blue-50 to-white text-brand-primary',
    cyan: 'from-cyan-50 to-white text-cyan-600',
    green: 'from-emerald-50 to-white text-emerald-600',
    amber: 'from-amber-50 to-white text-amber-600',
  }
  return (
    <div className="mp-stat-card mp-card-lift relative overflow-hidden rounded-2xl border p-5 shadow-card">
      <div className={clsx('absolute inset-x-0 top-0 h-1 bg-gradient-to-r', tones[tone])} />
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="mp-stat-label text-sm font-medium">{label}</p>
          <p className="mp-stat-value mt-2 text-2xl font-bold tracking-tight">{value}</p>
          {sub && <p className="mp-stat-sub mt-1 text-xs leading-5">{sub}</p>}
        </div>
        <span className={clsx('grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br', tones[tone])}>
          <i className={clsx('fas', icon)} aria-hidden="true" />
        </span>
      </div>
    </div>
  )
}

export function PageHeader({ eyebrow, title, description, actions, icon }) {
  return (
    <header className="mp-page-header flex flex-wrap items-start justify-between gap-5 rounded-3xl p-5 sm:p-6">
      <div className="flex min-w-0 items-start gap-4">
        {icon && (
          <span className="hidden h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-dark to-brand-primary text-white shadow-lg shadow-blue-900/15 sm:grid">
            <i className={clsx('fas', icon)} aria-hidden="true" />
          </span>
        )}
        <div>
          {eyebrow && <p className="mp-page-eyebrow mb-1 text-xs font-extrabold uppercase tracking-[0.16em]">{eyebrow}</p>}
          <h1 className="mp-page-title text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
          {description && <p className="mp-page-description mt-1.5 max-w-3xl text-sm leading-6">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  )
}

export function FormSection({ title, description, icon, children }) {
  return (
    <Card>
      <div className="mb-5 flex items-start gap-3 border-b border-slate-100 pb-4">
        {icon && <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-blue-50 text-brand-primary"><i className={clsx('fas', icon)} /></span>}
        <div>
          <h2 className="font-semibold text-brand-deep">{title}</h2>
          {description && <p className="mt-1 text-sm leading-5 text-slate-500">{description}</p>}
        </div>
      </div>
      {children}
    </Card>
  )
}

export function FormActions({ onCancel, saving, submitLabel = 'Salvar', children }) {
  return (
    <div className="sticky bottom-3 z-20 flex flex-wrap items-center justify-end gap-2 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-pop backdrop-blur">
      {children}
      <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancelar</Button>
      <Button type="submit" disabled={saving} icon={saving ? 'fa-spinner fa-spin' : 'fa-check'}>
        {saving ? 'Salvando…' : submitLabel}
      </Button>
    </div>
  )
}

export function EmptyState({ title = 'Nenhum registro encontrado', description, icon = 'fa-inbox', action }) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-2xl bg-blue-50 text-xl text-brand-primary"><i className={clsx('fas', icon)} /></span>
      <h3 className="mt-4 font-semibold text-brand-deep">{title}</h3>
      {description && <p className="mt-1 max-w-md text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function ConfirmAction({ onConfirm, label = 'Excluir', confirmLabel = 'Confirmar', variant = 'danger', className, disabled }) {
  const [confirming, setConfirming] = useState(false)
  if (!confirming) {
    return <Button variant={variant} className={className} disabled={disabled} onClick={() => setConfirming(true)}>{label}</Button>
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-xl border border-red-200 bg-red-50 p-1">
      <Button variant="ghost" className="!min-h-8 !px-2 !py-1 text-xs" onClick={() => setConfirming(false)}>Cancelar</Button>
      <Button variant="danger" className="!min-h-8 !px-2 !py-1 text-xs" onClick={async () => { await onConfirm(); setConfirming(false) }}>{confirmLabel}</Button>
    </span>
  )
}
